// Side-effect-free module for `zy-tiptap-utils`. Safe to import from Node export paths without auth/session/UI deps.

import {
    blockMathHTMLStaticRenderer,
    inlineMathHTMLStaticRenderer,
    zyCodeInlineStaticRenderer,
    zyDefnBlockHTMLStaticRenderer,
    zyDefnHTMLStaticRenderer,
    zyDefnTermHTMLStaticRenderer,
    zyListItemHTMLStaticRenderer,
    zyTermHTMLStaticRenderer,
    zyTextHTMLStaticRenderer,
} from '@/components/tiptap-editor/tiptap-extension/custom-static-renderer';
import type { Extensions, JSONContent } from '@tiptap/core';
import type { Mark, Node } from '@tiptap/pm/model';
import type { TiptapStaticRendererOptions } from './tiptap-static-renderer/json/renderer';
import { renderToHTMLString } from './tiptap-static-renderer/pm/html-string/html-string';
import { renderToMarkdown } from './tiptap-static-renderer/pm/markdown/markdown';

export type SelectedZyVersionFilter = string | string[] | null | undefined;

/**
 * Gets all descendant nodes of a specific type (recursive search)
 */
export function getDescendantOfType(node: JSONContent, descendantNodeName: string): JSONContent[] {
    const results: JSONContent[] = [];

    if (node.type === descendantNodeName) {
        results.push(node);
    }

    if (node.content && Array.isArray(node.content)) {
        for (const child of node.content) {
            results.push(...getDescendantOfType(child, descendantNodeName));
        }
    }

    return results;
}

/**
 * Normalizes a version filter (legacy string, multi-select array, or null) to a
 * sorted, deduped list of lowercase tokens, `[]` when the filter explicitly selects
 * nothing, or `null` when no filter is active (show all).
 */
export function normalizeSelectedZyVersionFilter(value: SelectedZyVersionFilter): string[] | null {
    if (value === null || value === undefined) {
        return null;
    }
    const tokens: string[] = [];
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return [];
        }
        for (const entry of value) {
            if (typeof entry !== 'string') continue;
            for (const part of entry.split(',')) {
                const t = part.trim().toLowerCase();
                if (t) tokens.push(t);
            }
        }
    } else {
        const s = value.trim();
        if (s === '') {
            return null;
        }
        for (const part of s.split(',')) {
            const t = part.trim().toLowerCase();
            if (t) tokens.push(t);
        }
    }
    const unique = [...new Set(tokens)].sort();
    return unique.length === 0 ? null : unique;
}

/**
 * Returns whether a zyVersion node should be visible given the document's selected version filter.
 */
export function isZyVersionNodeVisible(selectedZyVersion: SelectedZyVersionFilter, nodeVersion: string): boolean {
    const filterTokens = normalizeSelectedZyVersionFilter(selectedZyVersion);
    if (filterTokens === null) {
        return true;
    }
    const filterSet = new Set(filterTokens);
    const nodeTokens = (nodeVersion || '')
        .split(',')
        .map(t => t.trim().toLowerCase())
        .filter(Boolean);
    if (nodeTokens.length === 0) {
        return true;
    }
    return nodeTokens.some(t => filterSet.has(t));
}

const extractImageFilenameFromSrc = (src: string): string => {
    const imageSegment = src.split('IMAGES/')[1];
    if (!imageSegment) {
        return '';
    }
    return imageSegment.split('?')[0];
};

export const normalizeImportedImageSrc = (attrs: Record<string, any>, docAttrs?: Record<string, any> | null): string => {
    const src = attrs.src ?? '';
    const baseImageUrl = docAttrs?.baseImageUrl;
    if (!baseImageUrl || typeof baseImageUrl !== 'string') {
        return src;
    }

    const invalidImportedImageSrcPatterns = [/\/zyAuthor\/\[object Object\]\//, /\/zyAuthor\/ExampleZybookCode\//];
    const hasInvalidImportedSrc = !src || invalidImportedImageSrcPatterns.some(pattern => pattern.test(src));
    const fileNameFromSrc = typeof src === 'string' ? extractImageFilenameFromSrc(src) : '';
    const fileName = fileNameFromSrc || attrs.image_id || attrs.filename || '';

    const isZytoolsImage = typeof src === 'string' && src.includes('zytools.zybooks.com/zyAuthor/') && src.includes('/IMAGES/');
    const uploadImagesToMATESTBucket = docAttrs?.uploadImagesToMATESTBucket === true;
    const hasBaseUrlMismatch = !uploadImagesToMATESTBucket && isZytoolsImage && !src.startsWith(baseImageUrl);

    if (!hasInvalidImportedSrc && !hasBaseUrlMismatch) {
        return src;
    }

    if (!fileName) {
        return src;
    }

    return `${baseImageUrl}${fileName}`;
};

/**
 * Picks the initial toolbar version filter from subject defaults and the allowed list from the API.
 */
export function resolveInitialZyVersionFilter(
    validVersions: readonly string[],
    subjectVersion: string | null | undefined,
): string[] | null {
    if (validVersions.length === 0) {
        return null;
    }
    const preferred = subjectVersion?.trim().toLowerCase();
    if (preferred && validVersions.includes(preferred)) {
        return [preferred];
    }
    return [validVersions[0]];
}

/**
 * Normalize a version string by stripping whitespace around each entry,
 * removing empty entries, deduplicating, sorting the entries alphabetically,
 * and joining them with commas (no spaces).
 */
export function normalizeVersion(version: string): string {
    return [
        ...new Set(
            version
                .split(',')
                .map(v => v.trim().toLowerCase())
                .filter(Boolean),
        ),
    ]
        .sort()
        .join(',');
}

/**
 * Splits a normalized zyVersion attribute into sorted unique tokens.
 * @param version - The zyVersion attribute to parse.
 * @returns An array of sorted unique tokens.
 */
export function parseVersionAttrTokens(version: string): string[] {
    const normalized = normalizeVersion(version);
    if (!normalized) {
        return [];
    }
    return normalized.split(',');
}

export function renderJsonTo(
    currentJSON: Node | JSONContent,
    extensions: Extensions,
    format: 'html' | 'markdown',
    options: Partial<TiptapStaticRendererOptions<string, Mark, Node>> = {
        nodeMapping: {
            zydefn: zyDefnHTMLStaticRenderer,
            zydefnBlock: zyDefnBlockHTMLStaticRenderer,
            zydefTerm: zyDefnTermHTMLStaticRenderer,
            zyTerm: zyTermHTMLStaticRenderer,
            inlineMath: inlineMathHTMLStaticRenderer,
            blockMath: blockMathHTMLStaticRenderer,
            text: zyTextHTMLStaticRenderer,
            listItem: zyListItemHTMLStaticRenderer,
        },
        markMapping: {
            code: zyCodeInlineStaticRenderer,
        },
    },
): string {
    return format === 'html'
        ? renderToHTMLString({
              content: currentJSON,
              extensions,
              options,
          })
        : renderToMarkdown({
              content: currentJSON,
              extensions,
              options,
          });
}
